import { MasterCardTravelFormData, MasterCardUserFormData } from 'components';
import { DictionaryContext } from '../../../context/Dictionary';
import { PageContext, PageProps } from '../../../context/PageContext';
import React, { useContext, useEffect, useState } from 'react';
import { FactboxItemProps } from 'shared';
import { toKebabCase } from 'utilities';
import { _MasterCard } from '../../../dictionaryItems';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
	getMasterCardConfig,
	getMasterCardPrice,
	// selectCustomerReceiptSent,
	selectMasterCardCalculationStatus,
	selectMasterCardConfig,
	selectMasterCardConfigStatus,
	selectMasterCardPaymentId,
	selectPriceSummary,
	selectTravelFormData,
	selectUserFormData,
	setPaymentId,
	setTravelFormData,
	setUserFormData,
	// setCustomerReceiptSent,
	sendCustomerReceipt,
	sendErrorMail,
} from '../../../store/slices/mastercardSlice';
import { getMasterCardPaymentUrl, notifyMasterCardUser } from '../../../utilities/mastercardQueries';
import { MasterCardPage } from './MasterCardPage';
import { mapCustomerReceipt } from '../../../utilities/mapCustomerReceipt';

export type MasterCardListType = 'travel' | 'user' | 'price';

export interface PaymentDetails {
	amount: number;
	cardName: string;
	date: Date;
	id: string;
	orderNumber: string;
	dibsId: string;
	dibsTransactionNumber: string;
	mailDidSend: boolean;
}
export interface MasterCardPageAPIProps extends PageProps {
	bannerImage: Image;
	conditionsHeading: string;
	conditionsText: string;
	description?: string;
	heading: string;
	image: Image;
	masterCardAttentionIcon: BannerImage;
	masterCardCallIcon: Image;
	masterCardType: MasterCardType;
	paymentProviders: FactboxItemProps[];
	termsUrl: Link;
}

export const MasterCardPageAPI: React.FC<MasterCardPageAPIProps> = (props) => {
	const pageContext = useContext(PageContext);

	const { masterCardType, termsUrl, brand, pageModel } = pageContext;
	const [currentStep, setCurrentStep] = useState(1);
	const [paymentError, setPaymentError] = useState(false);

	const dispatch = useAppDispatch();
	const dictionary = useContext(DictionaryContext);
	const cardTypeGroup: MasterCardTypeGroup =
		masterCardType === 'MS' || masterCardType === 'MSB' || masterCardType === 'MSBN'
			? 'standard'
			: masterCardType === 'MG' || masterCardType === 'MGN' || masterCardType === 'MGB'
			? 'gold'
			: masterCardType === 'MP' ||
			  masterCardType === 'MPN' ||
			  masterCardType === 'MPB' ||
			  masterCardType === 'MPBN'
			? 'platinum'
			: masterCardType === 'WEM' || masterCardType === 'WEMN'
			? 'worldelite'
			: null;
	const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>(null);
	const cardConfig = useAppSelector((state) => selectMasterCardConfig(state, masterCardType));

	useEffect(() => {
		async function fetchConfig() {
			dispatch(getMasterCardConfig(masterCardType));
		}
		!cardConfig && fetchConfig();
	}, [dispatch, cardConfig]);

	const handleFormSubmit = (
		formData: MasterCardTravelFormData | MasterCardUserFormData,
		type: MasterCardListType,
	) => {
		if (type === 'travel') dispatch(setTravelFormData(formData as MasterCardTravelFormData));
		if (type === 'user') dispatch(setUserFormData(formData as MasterCardUserFormData));
	};

	const userFormData = useAppSelector(selectUserFormData);
	const priceSummary = useAppSelector(selectPriceSummary);
	const travelFormData = useAppSelector(selectTravelFormData);
	const configStatus = useAppSelector(selectMasterCardConfigStatus);
	const calculationStatus = useAppSelector(selectMasterCardCalculationStatus);
	const status = {
		configStatus,
		calculationStatus,
	};
	const paymentId = useAppSelector(selectMasterCardPaymentId);

	useEffect(() => {
		if (travelFormData) {
			dispatch(
				getMasterCardPrice({
					travelFormData,
					masterCardType: masterCardType,
				}),
			);
		}
	}, [dispatch, travelFormData, masterCardType]);

	const handlePayment = async () => {
		const cancelUrl = window.location.href.split('?')[0] + '?s=' + toKebabCase(_MasterCard(dictionary, 'Overview'));
		const returnUrl = window.location.href.split('?')[0] + '?s=' + toKebabCase(_MasterCard(dictionary, 'Receipt'));

		const paymentConfig = await getMasterCardPaymentUrl(masterCardType, returnUrl, termsUrl?.url, cancelUrl);
		if ('paymentId' in paymentConfig) {
			dispatch(setPaymentId(paymentConfig.paymentId));
			if (paymentConfig?.hostedPaymentPageUrl != undefined) {
				window.location.href = paymentConfig.hostedPaymentPageUrl;
			}
		} else {
			setPaymentError(true);
		}
	};

	// TODO KLK , do we need security for email receipts?
	// const customerReceiptSent = useAppSelector(selectCustomerReceiptSent);

	const handleEmail = async () => {
		try {
			const paymentDetails = await notifyMasterCardUser(
				masterCardType,
				paymentId,
				!pageContext.useCmsEmailCustomerReceipt,
			);

			if ('id' in paymentDetails) {
				// if (!customerReceiptSent) {
				if (pageContext.useCmsEmailCustomerReceipt) {
					const mappedCustomerMailDetails = mapCustomerReceipt(
						priceSummary,
						pageModel,
						travelFormData,
						userFormData,
						paymentDetails,
						cardConfig?.fullname,
						paymentId,
						brand,
					);
					paymentDetails.mailDidSend = await sendCustomerReceipt(mappedCustomerMailDetails);
				}
				// dispatch(setCustomerReceiptSent(true));
				// }
				setPaymentDetails(paymentDetails);
			}
			if (!(paymentDetails as PaymentDetails)?.mailDidSend || !(paymentDetails as PaymentDetails)?.id) {
				let msg = !(paymentDetails as PaymentDetails)?.mailDidSend ? 'No customer receipt email sent<br>' : '';
				msg += !(paymentDetails as PaymentDetails)?.id ? 'No id in paymentDetails' : '';
				sendError(
					priceSummary,
					pageModel,
					travelFormData,
					userFormData,
					paymentDetails,
					cardConfig?.fullname,
					paymentId,
					brand,
					msg,
				);
			}
		} catch (error) {
			sendError(
				priceSummary,
				pageModel,
				travelFormData,
				userFormData,
				paymentDetails,
				cardConfig?.fullname,
				paymentId,
				brand,
				'Unhandled error: ' + error.message,
			);
		}
	};

	const sendError = async (
		priceSummary,
		pageModel,
		travelFormData,
		userFormData,
		paymentDetails,
		cardConfigFullName,
		paymentId,
		brand,
		msg: string,
	) => {
		let mappedCustomerMailDetailsError;
		try {
			mappedCustomerMailDetailsError = mapCustomerReceipt(
				priceSummary,
				pageModel,
				travelFormData,
				userFormData,
				paymentDetails as PaymentDetails,
				cardConfigFullName,
				paymentId,
				brand,
			);
		} catch (error) {}
		if (!mappedCustomerMailDetailsError) {
			mappedCustomerMailDetailsError = 'No customer details available';
		}
		msg =
			'<h3>' +
			brand +
			'</h3><br><br>' +
			msg +
			'<br><br> CustomerDetails: ' +
			JSON.stringify(mappedCustomerMailDetailsError);
		sendErrorMail({ description: msg });
	};

	return (
		<MasterCardPage
			callbackPayment={handlePayment}
			callbackEmail={handleEmail}
			currentStep={currentStep}
			// errorIcon={errorIcon}
			setCurrentStep={setCurrentStep}
			formSubmit={handleFormSubmit}
			paymentDetails={paymentDetails}
			paymentError={paymentError}
			paymentId={paymentId}
			cardType={masterCardType}
			cardTypeGroup={cardTypeGroup}
			priceSummary={priceSummary}
			masterCardConfig={cardConfig && cardConfig}
			travelFormData={travelFormData}
			userFormData={userFormData}
			status={status}
			{...pageModel}
		/>
	);
};
